import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import { makeStyles } from '@mui/styles';

import { useApp } from 'contexts/AppContext';
import { useNotif } from 'contexts/NotifContext';
import { usePlatformConfig } from 'contexts/PlatformConfigContext';
import { useTheme } from 'contexts/ThemeContext';

import { adminRights as adminRightsConstant } from 'utils/constants';
import { activateAllRights, isValidEmail } from 'utils/functions';
import { ACTIVATION, AUTH, GROUP, USERS } from 'utils/rest';

import { HelpTooltip, Text } from 'components/DataDisplay';
import { Modal } from 'components/Feedback';
import { FormSection } from 'components/FormSection';
import {
  Button,
  Checkbox,
  CustomSelect,
  MultipleSelect,
  Select,
  Switch,
  TextField
} from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import { ListField } from 'components/ListField';
import { Loading } from 'components/Loading';

const ErrorState = {
  emailError: false,
  phoneNumberError: false,
  firstNameError: false,
  lastNameError: false,
  rolesError: false,
  productAccessError: false,
  shopNameError: false,
  addressError: false,
  externalIdError: false,
  shopsError: false,
  groupsError: false
};

const defaultRight = { ...adminRightsConstant };

const getSendCreationEmail = (
  userSendCreationEmail,
  domainSendCreationEmail
) => {
  if (typeof userSendCreationEmail === 'boolean') {
    return userSendCreationEmail;
  }
  if (typeof domainSendCreationEmail === 'boolean') {
    return domainSendCreationEmail;
  }
  return true;
};

const getShopId = shop => {
  if (!shop) {
    return null;
  }

  if (typeof shop === 'string') {
    return shop;
  }

  return shop._id || shop.id || shop.activationShopId || null;
};

const normalizeShop = shop => ({
  ...shop,
  _id: getShopId(shop),
  name: shop?.name || shop?.shopName || shop?.label || shop?.externalId || ''
});

const groupContainsShop = (group, shopId) => {
  return (group?.shops || []).some(shop => getShopId(shop) === shopId);
};

const getInitViewAndProfile = ({
  selectedUser,
  profileList,
  isSimplify,
  domainConfig
}) => {
  const isEditMode = !!selectedUser?.graviteeId;
  const defaultViewKey = isSimplify ? 'pml' : undefined;
  const defaultProfileKey = isSimplify
    ? domainConfig.defaultProfileKey
    : undefined;

  const viewKey = isEditMode ? selectedUser.viewKey : defaultViewKey;

  const initialFilteredProfiles = profileList.filter(item =>
    viewKey ? item.viewKey === viewKey : true
  );

  const profile = !isEditMode
    ? initialFilteredProfiles.find(item => item.key === defaultProfileKey)
        ?.key || initialFilteredProfiles[0]?.key
    : selectedUser.profile;

  return { profile, viewKey };
};

export const ModalForm = props => {
  const { notify } = useNotif();
  const { config, onValidate, onClose } = props;

  const { domainConfig, isSimplify } = useApp();
  const { country } = usePlatformConfig();
  const {
    selectedUser = {},
    rolesList,
    adminPresetList,
    domainList,
    shopGroups = [],
    networkGroups = [],
    viewList,
    profileList,
    allDataProviders = []
  } = config;

  const { graviteeId, firstConnection } = selectedUser;
  const isEditMode = !!graviteeId;
  const { themeColors } = useTheme();
  const [emailErrorMessage, setEmailErrorMessage] = useState('');
  const [groupsList, setGroupsList] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [menu, setMenu] = useState(0);
  const [shopsFromGroup, setShopsFromGroup] = useState([]);

  const timerRef = useRef(null);

  const useStyles = makeStyles({
    activeMenu: {
      maxWidth: 'fit-content',
      cursor: 'pointer',
      justifyContent: 'center',
      paddingBottom: '0px!important',
      borderBottom: `4px solid ${themeColors.global}`
    },
    defaultMenu: {
      justifyContent: 'center',
      maxWidth: 'fit-content',
      cursor: 'pointer',
      paddingBottom: '0px!important',
      borderBottom: 'none'
    }
  });
  const classes = useStyles();

  const defaultProfileKey = isSimplify
    ? domainConfig.defaultProfileKey
    : undefined;

  const ermesAccess = [
    {
      label: 'Accès interdit',
      value: 'blocked'
    },
    {
      label: 'Accès illimité',
      value: 'unlimited'
    },
    {
      label: 'Abonné',
      value: 'subscriber'
    }
  ];

  const initialState = {
    firstName: selectedUser.given_name || '',
    lastName: selectedUser.family_name || '',
    email: selectedUser.email || '',
    roles: selectedUser.roles?.[0]?.id || rolesList?.[0]?.id || [],
    groups: selectedUser.groups || [],
    adminRights: selectedUser.adminRights?.rights || defaultRight,
    adminRightsPreset: null,
    phoneNumber: selectedUser.phoneNumber || '',
    productAccess: selectedUser.productAccess || [
      {
        product: 'ermes',
        access: 'unlimited'
      }
    ],
    domains: selectedUser.domains || [],
    externalId: selectedUser.externalId || '',
    sendCreationEmail: getSendCreationEmail(
      selectedUser.sendCreationEmail,
      domainConfig?.features?.sendCreationEmail
    ),
    whatsappAgent: selectedUser?.whatsappAgent ?? true,
    canExportDataPartnerProfiles:
      selectedUser?.canExportDataPartnerProfiles || false,
    dataProviders:
      selectedUser?.dataProviders?.map(prov => ({ key: prov, label: prov })) ||
      [],
    shopName: '',
    adress: {},
    coordinates: { lat: '', lng: '' },
    siteUrl: '',
    shops: [],
    favorite: selectedUser.favoriteGroups?.[domainConfig?.domain],
    selectedNetwork: [],
    selectedShop: [],
    ...getInitViewAndProfile({
      selectedUser,
      profileList,
      isSimplify,
      domainConfig
    })
  };

  const [
    {
      firstName,
      lastName,
      email,
      phoneNumber,
      roles,
      groups,
      adminRights,
      adminRightsPreset,
      productAccess,
      sendCreationEmail,
      whatsappAgent,
      domains,
      externalId,
      canExportDataPartnerProfiles,
      dataProviders,
      shops,
      favorite,
      viewKey,
      profile,
      selectedNetwork,
      selectedShop
    },
    setState
  ] = useState(initialState);
  const [
    {
      emailError,
      phoneNumberError,
      firstNameError,
      lastNameError,
      rolesError,
      productAccessError,
      shopNameError,
      addressError,
      externalIdError,
      shopsError
    },
    setError
  ] = useState(ErrorState);

  const availableProfiles = profileList.filter(item =>
    viewKey ? item.viewKey === viewKey : true
  );

  const profileOptions = availableProfiles.map(item => ({
    label: item.name,
    value: item.key
  }));

  const capitalyse = str => {
    return str[0].toUpperCase() + str.substr(1);
  };

  const stringPathToObject = (object, keysStr) => {
    let keysString = '';
    keysString = keysStr.replace(/\[(\w+)\]/g, '.$1');
    keysString = keysStr.replace(/^\./, '');
    const keys = keysString.split('.');

    let objectReturn = object;
    for (let i = 0, len = keys.length; i < len; i += 1) {
      const key = keys[i];
      if (objectReturn[key] !== undefined) {
        objectReturn = objectReturn[key];
      } else {
        objectReturn[key] = {};
        objectReturn = objectReturn[key];
      }
    }
    return objectReturn;
  };

  const toggleError = (itemKey, state) => {
    setError(prevState => {
      const errors = { ...prevState };
      errors[`${itemKey}Error`] = state;
      return errors;
    });
  };

  const resetErrors = () => {
    setError(ErrorState);
  };
  const validatePhoneNumber = value => {
    const phoneRegex = {
      FR: /^(?:\+33|0)[1-9]\d{8}$/,
      BE: /^(?:\+32|0)[1-9]\d{7,8}$/
    };

    const regex = phoneRegex[country];
    // pas obligatoire
    if (!regex || !value) {
      return true;
    }

    return regex.test(value);
  };

  const selectedRoleConfig = rolesList.find(el => el.id === roles);
  const selectedRoleName =
    selectedRoleConfig?.name || selectedUser?.roles?.[0]?.name || '';
  const initialSelectedRoleName = selectedUser?.roles?.[0]?.name || '';
  const isMagasinRole = selectedRoleName === 'MAGASIN';
  const isInitialMagasinRole = initialSelectedRoleName === 'MAGASIN';
  const isSellerAdvStandardRole = ['SELLER', 'ADV', 'STANDARD'].includes(
    selectedRoleName
  );
  const selectedProfileConfig = availableProfiles.find(
    el => el.key === profile
  );

  const handleChange = itemKey => value => {
    const text = typeof value === 'string' ? value.trim() : value;
    const hasValue = Array.isArray(text) ? text.length > 0 : !!text;

    toggleError(itemKey, !hasValue);

    setState(prevState => {
      const states = { ...prevState };

      if (itemKey === 'selectedShop') {
        const selectedShopKeys = value.map(({ key }) => key);
        states.selectedShop = shopsFromGroup.filter(shop =>
          selectedShopKeys.includes(shop._id)
        );
      } else if (itemKey === 'selectedNetwork') {
        const selectedNetworkKeys = value.map(({ key }) => key);
        states.selectedNetwork = selectedNetworkKeys;
        states.selectedShop = [];
        if (selectedNetworkKeys.length > 0) {
          states.shops = [];
        }
      } else if (itemKey === 'viewKey') {
        const nextViewKey = text;
        const nextAvailableProfiles = profileList.filter(item =>
          nextViewKey ? item.viewKey === nextViewKey : true
        );

        const hasCurrentProfile = nextAvailableProfiles.some(
          item => item.key === states.profile
        );

        states.viewKey = nextViewKey;
        states.profile = hasCurrentProfile
          ? states.profile
          : nextAvailableProfiles.find(item => item.key === defaultProfileKey)
              ?.key ||
            nextAvailableProfiles[0]?.key ||
            '';
      } else {
        states[itemKey] = text;
      }

      if (itemKey === 'shops' && value.length > 0) {
        states.selectedNetwork = [];
        states.selectedShop = [];
      }

      return states;
    });
  };

  const handleChangeAccess = itemKey => value => {
    const newAccess = {
      product: itemKey,
      access: value
    };
    const index = productAccess.findIndex(el => el.product === itemKey);
    if (index === -1) {
      productAccess.push(newAccess);
    } else {
      productAccess[index] = newAccess;
    }
    setState(prevState => {
      const states = { ...prevState };
      states.productAccess = productAccess;
      return states;
    });
  };

  const handleChangePreset = value => {
    if (value === 'selectAll') {
      const allRights = activateAllRights(defaultRight);
      setState(prevState => {
        return {
          ...prevState,
          adminRightsPreset: value,
          adminRights: allRights
        };
      });
    } else {
      const rights = adminPresetList.find(el => el.id === value)?.rights;
      setState(prevState => {
        return {
          ...prevState,
          adminRightsPreset: value,
          adminRights: rights
        };
      });
    }
  };

  const handleCheckBoxChange = (masterKey, key) => () => {
    setState(prevState => {
      const states = { ...prevState };
      const oldState = stringPathToObject(states.adminRights, masterKey);
      oldState[key] = !oldState[key];
      return states;
    });
  };

  const resetStates = () => {
    setState(initialState);
    resetErrors();
  };

  const fetchGroups = async query => {
    const found = await GROUP.getGroupSearch({
      search: query
    });
    if (found?.data) {
      setGroupsList(found.data);
    }
  };

  const pollGroups = query => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      fetchGroups(query);
    }, 500);
  };

  const doAction = async () => {
    let error = false;
    resetErrors();
    const { domain } = domainConfig;

    const linkedNetworkGroups = selectedNetwork.reduce(
      (acc, activationShopGroupId) => {
        const linkedNetworkGroup = networkGroups.find(
          group => group.shopGroupId === activationShopGroupId
        );

        if (
          linkedNetworkGroup &&
          !acc.find(item => item._id === linkedNetworkGroup._id)
        ) {
          acc.push({
            _id: linkedNetworkGroup._id,
            name: linkedNetworkGroup.name
          });
        }

        return acc;
      },
      []
    );

    const groupsToSend =
      isSimplify && isMagasinRole && menu === 1 ? linkedNetworkGroups : groups;

    const datas = {
      firstName,
      lastName,
      email,
      roles: [roles],
      groups: groupsToSend,
      adminRights,
      productAccess,
      sendCreationEmail,
      additionalInformation: {
        domains: domains.filter(el => el !== 'selectAll'),
        externalId,
        whatsappAgent,
        phoneNumber,
        canExportDataPartnerProfiles,
        dataProviders: dataProviders.map(({ key }) => key),
        profile,
        viewKey,
        favoriteGroups: {
          ...(selectedUser?.favoriteGroups || {}),
          ...(groupsToSend.find(el => el._id === favorite)
            ? {
                [domainConfig.domain]: favorite
              }
            : { [domainConfig.domain]: null })
        }
      }
    };

    if (selectedRoleName !== 'ADMIN') {
      datas.additionalInformation.domain = domain;
    }

    const isAddUserToShop = shops.length === 0 && selectedShop.length > 0;
    let shopDatas;

    if (isAddUserToShop) {
      shopDatas = selectedShop.map(shop => {
        const linkedActivationShopGroup = shopGroups.find(
          group =>
            selectedNetwork.includes(group._id) &&
            groupContainsShop(group, shop._id)
        );
        const linkedGroup = shop.groupId;
        const linkedNetworkGroup = networkGroups.find(
          group => group.shopGroupId === linkedActivationShopGroup?._id
        );

        return {
          _id: linkedNetworkGroup?._id,
          activationShopGroupId: linkedActivationShopGroup?._id,
          group: linkedGroup,
          activationShopId: shop._id,
          users: shop.users || []
        };
      });
    } else {
      shopDatas = shops.map((shop, index) => ({
        name: shop?.shopName,
        address: shop?.address?.address,
        origin: {
          type: 'Point',
          coordinates: [shop?.address?.lng, shop?.address?.lat]
        },
        audienceType: 'radius',
        parameters: {
          unit: 'km',
          distance: '20'
        },
        key: `${externalId}_${index}`,
        siteUrl: shop?.siteUrl,
        domain: shop?.domain,
        shopGroup: shop?.shopGroup ? shop?.shopGroup : ''
      }));
    }

    setEmailErrorMessage('');
    if (!firstName) {
      toggleError('firstName', true);
      error = true;
    }
    if (!lastName) {
      toggleError('lastName', true);
      error = true;
    }
    if (!email?.trim()) {
      setEmailErrorMessage('Veuillez entrer une adresse email');
      toggleError('email', true);
      error = true;
    } else if (!isValidEmail(email)) {
      setEmailErrorMessage('Veuillez entrer une adresse email valide');
      toggleError('email', true);
      error = true;
    }
    if (!validatePhoneNumber(phoneNumber)) {
      toggleError('phoneNumber', true);
      error = true;
    }
    if (roles.length === 0) {
      toggleError('roles', true);
      error = true;
    }
    if (productAccess.length === 0) {
      toggleError('productAccess', true);
      error = true;
    }

    if (isSimplify) {
      if (!externalId && isMagasinRole) {
        toggleError('externalId', true);
        error = true;
      }

      if (menu === 0) {
        if (shops.length < 1 && groupsToSend.length < 1) {
          toggleError('shopsOrGroups', true);
          error = true;
        }
        if (shops.length > 1) {
          shops.forEach(shop => {
            if (!shop?.shopName) {
              toggleError('shopName', true);
              error = true;
            }
            if (
              !shop?.address?.address ||
              !shop?.address?.lat ||
              !shop?.address?.lng
            ) {
              toggleError('address', true);
              error = true;
            }
          });
        } else if (groupsToSend.length > 1) {
          groupsToSend.forEach(group => {
            if (!group.name) {
              toggleError('groupName', true);
              error = true;
            }
            if (!group._id) {
              toggleError('groupId', true);
              error = true;
            }
          });
        }
      } else if (selectedShop.length === 0) {
        toggleError('shops', true);
        error = true;
      }
    }

    if (!error) {
      try {
        await onValidate(datas, shopDatas, isAddUserToShop);
        resetStates();
      } catch (err) {
        const errorCode = err.response?.data?.code;
        if (errorCode === 409) {
          setEmailErrorMessage(
            'Cette adresse email est déjà utilisée. Choisissez-en une autre.'
          );
        } else if (errorCode === 400) {
          setEmailErrorMessage('Ce champ est obligatoire.');
        }
        toggleError('email', true);
      }
    }
  };

  const getActions = () => {
    return (
      <Container>
        <Row spacing={1}>
          <Item xs justify="flex-end">
            <Button
              disabled={!loaded}
              variant="contained"
              size="medium"
              widthSize="medium"
              onClick={doAction}
            >
              {graviteeId ? 'Modifier' : 'Créer'} l&lsquo;utilisateur
            </Button>
          </Item>
        </Row>
      </Container>
    );
  };

  const getCheckbox = (masterKey, sectionkeys) => {
    return sectionkeys.map(key => {
      return (
        <Row spacing={0}>
          <Item flex spacing={0}>
            <Checkbox
              style={{ padding: '1px' }}
              size="small"
              checked={
                stringPathToObject(adminRights, masterKey)?.[key] || false
              }
              onChange={handleCheckBoxChange(masterKey, key)}
            />
          </Item>
          <Item xs justify="flex-start" spacing={0}>
            <Text>
              {key === 'advanced' ? 'Advanced (restricted)' : capitalyse(key)}
            </Text>
          </Item>
        </Row>
      );
    });
  };

  const updateMenu = index => {
    setMenu(index);
    toggleError('shops', false);
  };

  const AdminRightSection = configAdminRight => {
    const { titleSection, masterKey, sectionkeys } = configAdminRight;
    return (
      <Item
        justify="flex-start"
        xs={4}
        style={{
          alignContent: 'flex-start',
          marginBottom: '20px'
        }}
      >
        <Row spacing={0}>
          <Item justify="flex-start">
            <Text bold>{titleSection}</Text>
          </Item>
        </Row>
        {getCheckbox(masterKey, sectionkeys)}
      </Item>
    );
  };

  const sendMailAccount = async () => {
    const method = firstConnection
      ? USERS.sendCreationMail
      : AUTH.resetPassword;
    const result = await method(email);
    const emailTypeMsg = firstConnection
      ? 'création de compte'
      : 'réinitialisation de mot de passe';

    if (!result) {
      return notify({
        message: `L'envoi d'email de ${emailTypeMsg} a échoué. Veuillez réessayer.`,
        variant: 'error'
      });
    }
    return notify({
      message: `Un email de ${emailTypeMsg} a été envoyé à l’utilisateur.`,
      variant: 'success'
    });
  };

  const loadData = async () => {
    const fetchedGroups = await GROUP.getGroupSearch({});
    if (fetchedGroups?.data) {
      setGroupsList(fetchedGroups.data);
    }

    setLoaded(true);
  };
  const lastGroupTaxonomyName = domainConfig.groupTaxonomy?.at(-1)?.name;
  const buttonLabel = `Ajouter votre ${lastGroupTaxonomyName}`;

  useEffect(() => {
    if (!loaded) {
      loadData();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchShopsFromGroup = async () => {
      const shopsIdsFromGroup = [
        ...new Set(
          shopGroups
            .filter(group => selectedNetwork.includes(group._id))
            .flatMap(group =>
              (group.shops || []).map(getShopId).filter(Boolean)
            )
        )
      ];

      if (!shopsIdsFromGroup.length) {
        if (isMounted) {
          setShopsFromGroup([]);
        }
        return;
      }

      try {
        const fetchedShops = await ACTIVATION.getActivationShopsByIds(
          shopsIdsFromGroup
        );

        if (isMounted) {
          setShopsFromGroup((fetchedShops || []).map(normalizeShop));
        }
      } catch (err) {
        if (isMounted) {
          setShopsFromGroup([]);
        }
      }
    };

    fetchShopsFromGroup();
    return () => {
      isMounted = false;
    };
  }, [selectedNetwork, shopGroups]);

  return (
    <Modal
      actions={getActions()}
      {...props}
      onClose={() => {
        resetStates();
        onClose();
      }}
    >
      <Loading loading={!loaded}>
        <Container>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Général" xs={3}>
                <Container>
                  <Row spacing={0}>
                    <Item xs={6} justify="flex-start">
                      <Container>
                        <Row spacing={0} justify="flex-start">
                          <Item xs={11} justify="flex-start">
                            <TextField
                              title="Prénom"
                              required
                              small
                              label=""
                              onChange={handleChange('firstName')}
                              value={firstName}
                              error={firstNameError}
                              bgColor={themeColors.primary}
                            />
                          </Item>
                        </Row>
                      </Container>
                    </Item>
                    <Item xs={6}>
                      <Container>
                        <Row spacing={0} justify="flex-start">
                          <Item xs={11} justify="flex-start">
                            <TextField
                              title="Nom"
                              required
                              small
                              label=""
                              onChange={handleChange('lastName')}
                              value={lastName}
                              error={lastNameError}
                              bgColor={themeColors.primary}
                            />
                          </Item>
                        </Row>
                      </Container>
                    </Item>
                  </Row>
                  <Row spacing={0}>
                    <Item xs={6} justify="flex-start">
                      <Container>
                        <Row spacing={0} justify="flex-start">
                          <Item xs={11} justify="flex-start">
                            <TextField
                              title="Email"
                              required
                              small
                              label=""
                              onChange={handleChange('email')}
                              value={email}
                              error={emailError}
                              errorText={emailErrorMessage}
                              disabled={!!graviteeId}
                              bgColor={themeColors.primary}
                            />
                          </Item>
                        </Row>
                      </Container>
                    </Item>
                    <Item
                      xs={6}
                      justify="flex-start"
                      style={{
                        marginBottom: '-25px'
                      }}
                    >
                      <Container>
                        <Row spacing={0} justify="flex-start">
                          <Item xs={11} justify="flex-start">
                            <TextField
                              title="ID Compte Utilisateur"
                              small
                              helperText={
                                isSimplify
                                  ? 'L’ID renseigné sera utilisé comme key du magasin'
                                  : undefined
                              }
                              required={isSimplify && isMagasinRole}
                              disabled={
                                isSimplify && isEditMode && isInitialMagasinRole
                              }
                              placeholder="ID FR-ACC"
                              onChange={handleChange('externalId')}
                              value={externalId}
                              error={externalIdError}
                              bgColor={themeColors.primary}
                            />
                          </Item>
                        </Row>
                      </Container>
                    </Item>
                  </Row>

                  <Row spacing={0} justify="flex-start">
                    <Item xs={6}>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="center">
                          <Button
                            disabled={!graviteeId}
                            onClick={sendMailAccount}
                            variant="contained"
                            startIcon={
                              <ForwardToInboxIcon
                                style={{
                                  color: !graviteeId
                                    ? themeColors.keepDark
                                    : themeColors.keep_light
                                }}
                              />
                            }
                          >
                            {`Mail ${!firstConnection ? 're' : ''}set password`}
                          </Button>
                        </Item>
                      </Row>
                    </Item>
                    {!isSimplify && (
                      <Item xs={6}>
                        <Row spacing={0} justify="flex-start">
                          <Item xs={11} justify="flex-start">
                            <TextField
                              title="Téléphone"
                              small
                              onChange={handleChange('phoneNumber')}
                              value={phoneNumber}
                              error={phoneNumberError}
                              format="phone"
                              errorText="Le numéro n’est pas reconnu sous ce format. Veuillez utiliser un format 0600000000 ou +33600000000."
                              bgColor={themeColors.primary}
                            />
                          </Item>
                        </Row>
                      </Item>
                    )}
                  </Row>

                  {!isSimplify && (
                    <Row spacing={0} justify="flex-start">
                      <Item justify="flex-start">
                        <Text
                          style={{
                            paddingRight: '5px'
                          }}
                        >
                          Envoi d’email à la création utilisateur
                        </Text>
                        <HelpTooltip
                          style={{
                            padding: '10px'
                          }}
                          title={`L’utilisateur ne recevra pas d’email lors de sa création
                        pour setuper son mot de passe et ne pourra pas le reset.`}
                        />
                        <Container
                          style={{
                            width: 'auto',
                            paddingLeft: '5px'
                          }}
                        >
                          <Switch
                            title=""
                            checked={sendCreationEmail}
                            onChange={handleChange('sendCreationEmail')}
                            options={['Non', 'Oui']}
                            disabled={Object.keys(selectedUser).length !== 0}
                          />
                        </Container>
                      </Item>
                    </Row>
                  )}

                  {!isSimplify && domainConfig?.features?.whatsappAgent && (
                    <Row spacing={0} justify="flex-start">
                      <Item justify="flex-start">
                        <Text
                          style={{
                            paddingRight: '5px'
                          }}
                        >
                          Notifications agent Ermes
                        </Text>
                        <Container
                          style={{
                            width: 'auto',
                            paddingLeft: '5px'
                          }}
                        >
                          <Switch
                            title=""
                            checked={whatsappAgent}
                            onChange={handleChange('whatsappAgent')}
                            options={['Non', 'Oui']}
                          />
                        </Container>
                      </Item>
                    </Row>
                  )}
                </Container>
              </FormSection>

              <FormSection title="Rôle et permissions" xs={3}>
                <Container justify="flex-start">
                  <Item xs={6}>
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <Select
                            title="Role"
                            required
                            onChange={handleChange('roles')}
                            options={rolesList.map(el => ({
                              label: el.name,
                              value: el.id
                            }))}
                            disabled={rolesList?.length <= 1}
                            error={rolesError}
                            value={roles}
                            small
                            bgColor={themeColors.primary}
                          />
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                  <Item xs={6}>
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <Select
                            title="Vue"
                            onChange={handleChange('viewKey')}
                            options={viewList.map(el => ({
                              label: el.label,
                              value: el.key
                            }))}
                            value={viewKey}
                            small
                            disabled={isSimplify}
                            bgColor={themeColors.primary}
                          />
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                  <Item xs={6}>
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <Select
                            title="Profil"
                            onChange={handleChange('profile')}
                            options={profileOptions}
                            value={profile}
                            small
                            disabled={profileOptions.length === 0}
                            bgColor={themeColors.primary}
                            tooltip={selectedProfileConfig?.description}
                          />
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                  {!isSimplify && (
                    <Item xs={6}>
                      <Container>
                        <Row spacing={0} justify="flex-start">
                          <Item xs={11} justify="flex-start">
                            <MultipleSelect
                              title="ID partenaire"
                              small
                              options={allDataProviders.map(el => ({
                                label: el,
                                key: el
                              }))}
                              selected={dataProviders.map(el => el.key)}
                              onChange={handleChange('dataProviders')}
                              disabled={!canExportDataPartnerProfiles}
                            />
                          </Item>
                        </Row>
                      </Container>
                    </Item>
                  )}
                  {!isSimplify && (
                    <Item xs={6}>
                      <Container>
                        <Row spacing={0} justify="flex-start">
                          <Item justify="flex-start">
                            <Text
                              style={{
                                paddingRight: '5px'
                              }}
                            >
                              Peut exporter des ID Partenaire
                            </Text>
                            <Container
                              style={{
                                width: 'auto',
                                paddingLeft: '5px'
                              }}
                            >
                              <Switch
                                title=""
                                checked={canExportDataPartnerProfiles}
                                options={['Non', 'Oui']}
                                onChange={handleChange(
                                  'canExportDataPartnerProfiles'
                                )}
                              />
                            </Container>
                          </Item>
                        </Row>
                      </Container>
                    </Item>
                  )}
                </Container>
              </FormSection>
            </Item>
          </Row>

          {!isSimplify && (
            <Row spacing={1}>
              <Item justify="flex-start">
                <FormSection title="Accès aux produits" xs={3}>
                  <Container>
                    <Row spacing={0} justify="flex-start">
                      <Item xs={6} justify="flex-start">
                        <Row spacing={0} justify="flex-start">
                          <Item xs={11} justify="flex-start">
                            <CustomSelect
                              small
                              title="Domaine"
                              options={domainList}
                              selected={domainList.filter(el =>
                                domains.includes(el.key)
                              )}
                              onChange={values => {
                                const newValues = values.map(
                                  value => value.key
                                );
                                // certain ancien utilisateur n'on pas de domain en db et l'option
                                // lean de la methode getAll de l'api n'applique pas le default
                                const userDomain =
                                  selectedUser?.domain || 'ERMES';
                                if (!newValues.includes(userDomain)) {
                                  newValues.push(userDomain);
                                }
                                handleChange('domains')(
                                  values.map(value => value.key)
                                );
                              }}
                              selectAll
                              hideNone
                              width="382px"
                              searchBar
                              type="domain"
                              textColor={themeColors.global}
                              limit={domainList.length}
                              multiple
                            />
                          </Item>
                        </Row>
                      </Item>
                      <Item xs={6} justify="flex-end">
                        <Container>
                          <Row spacing={0} justify="flex-start">
                            <Item xs={11} justify="flex-start">
                              <Select
                                title="Ermes"
                                required
                                onChange={handleChangeAccess('ermes')}
                                options={ermesAccess}
                                error={productAccessError}
                                value={
                                  productAccess.find(
                                    el => el.product === 'ermes'
                                  )?.access
                                }
                                small
                                bgColor={themeColors.primary}
                              />
                            </Item>
                          </Row>
                        </Container>
                      </Item>
                    </Row>
                  </Container>
                </FormSection>
              </Item>
            </Row>
          )}

          {(!isSimplify || (isSimplify && isSellerAdvStandardRole)) && (
            <Row spacing={1}>
              <Item justify="flex-start">
                <FormSection
                  title={!isSimplify ? 'Organisation' : lastGroupTaxonomyName}
                  xs={3}
                >
                  <Container>
                    <Row spacing={0} justify="flex-start">
                      <Item justify="flex-start">
                        <ListField
                          options={groupsList.map(item => ({
                            _id: item?._id,
                            name: item?.name
                          }))}
                          searchOptionsKeys={['name', '_id']}
                          values={groups.map(item => ({
                            _id: item?._id,
                            name: item?.name
                          }))}
                          onChangeInput={value => {
                            pollGroups(value);
                          }}
                          onChange={handleChange('groups')}
                          actionTxt={
                            !isSimplify
                              ? 'Ajouter une organisation'
                              : buttonLabel
                          }
                          handleFavorite={handleChange('favorite')}
                          favoriteValue={favorite}
                          deleteAction
                          variant="asyncAutoComplete"
                        />
                      </Item>
                    </Row>
                  </Container>
                </FormSection>
              </Item>
            </Row>
          )}

          {selectedRoleName === 'ADMIN' && (
            <Row spacing={1}>
              <Item justify="flex-start">
                <FormSection title="Droits de l'utilisateur" xs={3}>
                  <Row spacing={0} justify="flex-start">
                    <Item xs={6}>
                      <Container>
                        <Row spacing={0} justify="flex-start">
                          <Item xs={11} justify="flex-start">
                            <Select
                              title="Preset"
                              onChange={handleChangePreset}
                              options={[
                                {
                                  label: 'Tous les droits',
                                  value: 'selectAll'
                                },
                                ...adminPresetList.map(el => ({
                                  label: el.name,
                                  value: el.id
                                }))
                              ]}
                              value={adminRightsPreset}
                              small
                              bgColor={themeColors.primary}
                              defaultValue="Sélectionnez un preset"
                            />
                          </Item>
                        </Row>
                      </Container>
                    </Item>
                  </Row>
                  <Row
                    spacing={0}
                    justify="center"
                    style={{
                      marginBottom: '20px'
                    }}
                  >
                    <Item justify="flex-start">
                      <Text bold fontSize="16px">
                        Mesure
                      </Text>
                    </Item>
                    <Item xs={10} alignItems="flex-start" justify="flex-start">
                      <AdminRightSection
                        titleSection="Géneral"
                        masterKey="funnel.funnel"
                        sectionkeys={Object.keys(defaultRight.funnel.funnel)}
                      />
                      <AdminRightSection
                        titleSection="Models"
                        masterKey="funnel.models"
                        sectionkeys={Object.keys(defaultRight.funnel.models)}
                      />
                      <AdminRightSection
                        titleSection="Data"
                        masterKey="funnel.data"
                        sectionkeys={Object.keys(defaultRight.funnel.data)}
                      />
                      <AdminRightSection
                        titleSection="Médias"
                        masterKey="funnel.medias"
                        sectionkeys={Object.keys(defaultRight.funnel.medias)}
                      />
                      <AdminRightSection
                        titleSection="Propriétés"
                        masterKey="funnel.properties"
                        sectionkeys={Object.keys(
                          defaultRight.funnel.properties
                        )}
                      />
                      <AdminRightSection
                        titleSection="Modèles"
                        masterKey="funnel.funnelStep"
                        sectionkeys={Object.keys(
                          defaultRight.funnel.funnelStep
                        )}
                      />
                      <AdminRightSection
                        titleSection="Metric Yougov"
                        masterKey="funnel.yougovMetrics"
                        sectionkeys={Object.keys(
                          defaultRight.funnel.yougovMetrics
                        )}
                      />
                      <AdminRightSection
                        titleSection="Anomalies"
                        masterKey="funnel.anomalies"
                        sectionkeys={Object.keys(defaultRight.funnel.anomalies)}
                      />
                      <AdminRightSection
                        titleSection="Connecteurs"
                        masterKey="funnel.connectors"
                        sectionkeys={Object.keys(
                          defaultRight.funnel.connectors
                        )}
                      />
                      <AdminRightSection
                        titleSection="Boucle Retour Data"
                        masterKey="funnel.dataFeedback"
                        sectionkeys={Object.keys(
                          defaultRight.funnel.dataFeedback
                        )}
                      />
                      <AdminRightSection
                        titleSection="Alerting"
                        masterKey="funnel.alert"
                        sectionkeys={Object.keys(defaultRight.funnel.alert)}
                      />
                      <AdminRightSection
                        titleSection="Export CSV BO"
                        masterKey="funnel.backOfficeCsvExport"
                        sectionkeys={Object.keys(
                          defaultRight.funnel.backOfficeCsvExport
                        )}
                      />
                    </Item>
                  </Row>
                  <Row
                    spacing={0}
                    justify="center"
                    style={{
                      marginBottom: '20px'
                    }}
                  >
                    <Item justify="flex-start">
                      <Text bold fontSize="16px">
                        Data
                      </Text>
                    </Item>
                    <Item xs={10} alignItems="flex-start" justify="flex-start">
                      <AdminRightSection
                        titleSection="Critères"
                        masterKey="audiences.criteria"
                        sectionkeys={Object.keys(
                          defaultRight.audiences.criteria
                        )}
                      />
                      <AdminRightSection
                        titleSection="Catégories"
                        masterKey="audiences.categories"
                        sectionkeys={Object.keys(
                          defaultRight.audiences.categories
                        )}
                      />
                      <AdminRightSection
                        titleSection="TagFunnel"
                        masterKey="audiences.tagFunnel"
                        sectionkeys={Object.keys(
                          defaultRight.audiences.tagFunnel
                        )}
                      />
                      <AdminRightSection
                        titleSection="Audiences Activées"
                        masterKey="audiences.activatedAudience"
                        sectionkeys={Object.keys(
                          defaultRight.audiences.activatedAudience
                        )}
                      />
                      <AdminRightSection
                        titleSection="Export Segment"
                        masterKey="audiences.exportSegment"
                        sectionkeys={Object.keys(
                          defaultRight.audiences.exportSegment
                        )}
                      />
                      <AdminRightSection
                        titleSection="Export Media"
                        masterKey="audiences.exportMedia"
                        sectionkeys={Object.keys(
                          defaultRight.audiences.exportMedia
                        )}
                      />
                    </Item>
                  </Row>
                  <Row
                    spacing={0}
                    justify="center"
                    style={{
                      marginBottom: '20px'
                    }}
                  >
                    <Item justify="flex-start">
                      <Text bold fontSize="16px">
                        Général
                      </Text>
                    </Item>
                    <Item xs={10} alignItems="flex-start" justify="flex-start">
                      <AdminRightSection
                        titleSection="Dashboard"
                        masterKey="general.dashboard"
                        sectionkeys={Object.keys(
                          defaultRight.general.dashboard
                        )}
                      />
                      <AdminRightSection
                        titleSection="Organisations"
                        masterKey="general.groups"
                        sectionkeys={Object.keys(defaultRight.general.groups)}
                      />
                      <AdminRightSection
                        titleSection="Utilisateurs"
                        masterKey="general.users"
                        sectionkeys={Object.keys(defaultRight.general.users)}
                      />
                      <AdminRightSection
                        titleSection="Connection"
                        masterKey="general.connection"
                        sectionkeys={Object.keys(
                          defaultRight.general.connection
                        )}
                      />
                      <AdminRightSection
                        titleSection="Administrateurs"
                        masterKey="general.admin"
                        sectionkeys={Object.keys(defaultRight.general.admin)}
                      />
                      <AdminRightSection
                        titleSection="Droit des administrateurs"
                        masterKey="general.adminRights"
                        sectionkeys={Object.keys(
                          defaultRight.general.adminRights
                        )}
                      />
                      <AdminRightSection
                        titleSection="Connector"
                        masterKey="general.connector"
                        sectionkeys={Object.keys(
                          defaultRight.general.connector
                        )}
                      />
                      <AdminRightSection
                        titleSection="Domaine"
                        masterKey="general.domain"
                        sectionkeys={Object.keys(defaultRight.general.domain)}
                      />
                      <AdminRightSection
                        titleSection="Médias activables"
                        masterKey="general.activableMedias"
                        sectionkeys={Object.keys(
                          defaultRight.general.activableMedias
                        )}
                      />
                      <AdminRightSection
                        titleSection="Clients"
                        masterKey="general.customers"
                        sectionkeys={Object.keys(
                          defaultRight.general.customers
                        )}
                      />
                      <AdminRightSection
                        titleSection="Tarification"
                        masterKey="general.tarification"
                        sectionkeys={Object.keys(
                          defaultRight.general.tarification
                        )}
                      />
                      <AdminRightSection
                        titleSection="Monday"
                        masterKey="general.monday"
                        sectionkeys={Object.keys(defaultRight.general.monday)}
                      />
                      <AdminRightSection
                        titleSection="Logs"
                        masterKey="logs"
                        sectionkeys={Object.keys(defaultRight.logs)}
                      />
                      <AdminRightSection
                        titleSection="God mode"
                        masterKey="godMode"
                        sectionkeys={Object.keys(defaultRight.godMode)}
                      />
                    </Item>
                  </Row>
                  <Row
                    spacing={0}
                    justify="center"
                    style={{
                      marginBottom: '20px'
                    }}
                  >
                    <Item justify="flex-start">
                      <Text bold fontSize="16px">
                        PML
                      </Text>
                    </Item>
                    <Item xs={10} alignItems="flex-start" justify="flex-start">
                      <AdminRightSection
                        titleSection="Configuration"
                        masterKey="pml.config"
                        sectionkeys={Object.keys(defaultRight.pml.config)}
                      />
                      <AdminRightSection
                        titleSection="Points de ventes"
                        masterKey="pml.shop"
                        sectionkeys={Object.keys(defaultRight.pml.shop)}
                      />
                      <AdminRightSection
                        titleSection="Groupes de magasins"
                        masterKey="pml.shopGroup"
                        sectionkeys={Object.keys(defaultRight.pml.shopGroup)}
                      />
                      <AdminRightSection
                        titleSection="Newsletter"
                        masterKey="pml.newsletter"
                        sectionkeys={Object.keys(defaultRight.pml.newsletter)}
                      />
                    </Item>
                  </Row>
                  <Row
                    spacing={0}
                    justify="center"
                    style={{
                      marginBottom: '20px'
                    }}
                  >
                    <Item justify="flex-start">
                      <Text bold fontSize="16px">
                        Media
                      </Text>
                    </Item>
                    <Item xs={10} alignItems="flex-start" justify="flex-start">
                      <AdminRightSection
                        titleSection="Presets"
                        masterKey="activation"
                        sectionkeys={Object.keys(defaultRight.activation)}
                      />
                      <AdminRightSection
                        titleSection="Campagnes"
                        masterKey="campaign.campaign"
                        sectionkeys={Object.keys(
                          defaultRight.campaign.campaign
                        )}
                      />
                    </Item>
                  </Row>
                  <Row
                    spacing={0}
                    justify="center"
                    style={{
                      marginBottom: '20px'
                    }}
                  >
                    <Item justify="flex-start">
                      <Text bold fontSize="16px">
                        Content
                      </Text>
                    </Item>
                    <Item xs={10} alignItems="flex-start" justify="flex-start">
                      <AdminRightSection
                        titleSection="Formats"
                        masterKey="content.formats"
                        sectionkeys={Object.keys(defaultRight.content.formats)}
                      />
                    </Item>
                  </Row>
                </FormSection>
              </Item>
            </Row>
          )}

          {isSimplify && !isEditMode && isMagasinRole && (
            <Row spacing={1}>
              <Item justify="flex-start">
                <FormSection
                  title="Points de ventes"
                  xs={3}
                  tooltip="Vous devez obligatoirement renseigner au moins 1 point de vente"
                >
                  <>
                    <Row
                      style={{
                        paddingBottom: '10px'
                      }}
                    >
                      <Item
                        xs={6}
                        className={
                          menu === 0 ? classes.activeMenu : classes.defaultMenu
                        }
                        onClick={() => updateMenu(0)}
                      >
                        <Text variant="h5" bold={menu === 0}>
                          Créer un point de vente
                        </Text>
                      </Item>
                      <Item
                        xs={6}
                        className={
                          menu === 1 ? classes.activeMenu : classes.defaultMenu
                        }
                        onClick={() => updateMenu(1)}
                      >
                        <Text variant="h5" bold={menu === 1}>
                          Rattacher à un point de vente existant
                        </Text>
                      </Item>
                    </Row>
                  </>
                  {menu === 0 ? (
                    <>
                      <ListField
                        values={shops}
                        options={[
                          'shopName',
                          'address',
                          'siteUrl',
                          'shopGroup'
                        ]}
                        labels={[
                          'Nom du Magasin',
                          'Adresse du magasin',
                          'URL du site',
                          'Rattacher à un réseau'
                        ]}
                        inputs={[
                          'TextField',
                          'LocationSearchInput',
                          'TextField',
                          'Select'
                        ]}
                        variant="customFields"
                        fieldsFormat={[
                          null,
                          null,
                          null,
                          shopGroups.map(el => ({
                            label: el.name,
                            value: el._id
                          }))
                        ]}
                        isRequireds={[true, true, false, true]}
                        deleteAction
                        onChange={handleChange('shops')}
                        actionTxt="Ajouter un magasin"
                        initialAction={
                          isSimplify && !graviteeId && isMagasinRole
                        }
                      />
                      {(shopsError || addressError || shopNameError) &&
                        selectedShop.length === 0 && (
                          <Text color={themeColors.error} bold>
                            Pour créer l&apos;utilisateur, créez un point de
                            vente associé
                          </Text>
                        )}
                    </>
                  ) : (
                    <>
                      <Item
                        xs={3}
                        justify="flex-start"
                        style={{
                          paddingLeft: '24px'
                        }}
                      >
                        <CustomSelect
                          small
                          title={lastGroupTaxonomyName}
                          hideNone
                          options={shopGroups.map(el => ({
                            label: el.name,
                            key: el._id,
                            value: el._id
                          }))}
                          selected={selectedNetwork}
                          onChange={handleChange('selectedNetwork')}
                          searchBar
                          required
                          selectAll={false}
                          type={lastGroupTaxonomyName}
                          value={selectedNetwork}
                          multiple
                        />
                      </Item>
                      <Item
                        xs={3}
                        justify="flex-start"
                        style={{
                          paddingLeft: '24px'
                        }}
                      >
                        <CustomSelect
                          small
                          title="Magasin"
                          hideNone
                          options={shopsFromGroup.map(shop => ({
                            label: shop.name,
                            key: shop._id,
                            value: shop._id
                          }))}
                          selected={selectedShop.map(shop => shop._id)}
                          onChange={handleChange('selectedShop')}
                          searchBar
                          selectAll={false}
                          disabled={
                            selectedNetwork.length === 0 || shops.length > 0
                          }
                          required
                          type="Magasin"
                          multiple
                        />
                      </Item>
                      {shopsError && selectedShop.length === 0 && (
                        <Text color={themeColors.error} bold>
                          Pour créer l&apos;utilisateur, veuillez le rattacher à
                          un point de vente
                        </Text>
                      )}
                    </>
                  )}
                </FormSection>
              </Item>
            </Row>
          )}
        </Container>
      </Loading>
    </Modal>
  );
};

ModalForm.defaultProps = {
  onClose: () => null,
  onValidate: () => {}
};
ModalForm.propTypes = {
  onCreate: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  onValidate: PropTypes.func,
  config: PropTypes.objectOf({
    groupsList: PropTypes.arrayOf(PropTypes.any).isRequired,
    adminPresetList: PropTypes.arrayOf(PropTypes.any).isRequired,
    rolesList: PropTypes.arrayOf(PropTypes.any).isRequired,
    selectedUser: PropTypes.arrayOf(PropTypes.any)
  }).isRequired
};

export default ModalForm;
